import React from 'react';
import { Select, SelectProps, MenuItem, SelectChangeEvent } from '@mui/material';

export interface Option<T extends string | number> {
  label: string;
  value: T;
}

interface SingleSelectProps<T extends string | number>
  extends Omit<SelectProps<T>, 'onChange' | 'value'> {
  value: T | '';
  options: Option<T>[];
  onChange: (value: T) => void;
  renderValue?: (value: T) => React.ReactNode;
  renderOption?: (option: Option<T>, selected: boolean) => React.ReactNode;
}

const SingleSelect = <T extends string | number>({
  value,
  options,
  onChange,
  renderValue,
  renderOption,
  ...rest
}: SingleSelectProps<T>) => {
  const handleChange = (e: SelectChangeEvent<unknown>) => {
    onChange(e.target.value as T);
  };

  return (
    <Select<T>
      value={value}
      onChange={handleChange}
      renderValue={
        renderValue
          ? val => renderValue(val as T)
          : val => options.find(opt => opt.value === val)?.label || ''
      }
      {...rest}
    >
      {options.map(option => {
        const selected = option.value === value;

        return (
          <MenuItem key={option.value} value={option.value} disableGutters dense>
            {renderOption ? renderOption(option, selected) : option.label}
          </MenuItem>
        );
      })}
    </Select>
  );
};

export default SingleSelect;
